import * as React from "react"

interface Options {
  /** Numero de filas navegables. */
  count: number
  /** Enter sobre la fila enfocada. */
  onActivate?: (index: number) => void
  /** Espacio sobre la fila enfocada (seleccionar / marcar). */
  onToggle?: (index: number) => void
  /** Flecha derecha / izquierda: desplegar y plegar el detalle. */
  onExpand?: (index: number, expanded: boolean) => void
}

/**
 * Navegacion por teclado en tablas densas.
 *
 * Un contador que revisa 300 comprobantes no quiere soltar el teclado para
 * apuntar con el mouse a una fila de 28px. Flechas para moverse, Enter para
 * abrir, Espacio para seleccionar, flechas laterales para ver el fundamento.
 *
 * Roving tabindex: solo la fila del cursor es tabulable, asi Tab sigue saltando
 * de la tabla al siguiente control en vez de recorrer 300 filas.
 */
export function useTableKeyboardNav({ count, onActivate, onToggle, onExpand }: Options) {
  const [cursor, setCursor] = React.useState(0)
  const bodyRef = React.useRef<HTMLTableSectionElement>(null)

  React.useEffect(() => {
    if (count > 0 && cursor > count - 1) setCursor(count - 1)
  }, [count, cursor])

  const focusRow = React.useCallback((index: number) => {
    const row = bodyRef.current?.querySelector<HTMLTableRowElement>(`[data-row-index="${index}"]`)
    if (!row) return
    row.focus({ preventScroll: true })
    row.scrollIntoView({ block: "nearest" })
  }, [])

  const move = React.useCallback(
    (index: number) => {
      const next = Math.max(0, Math.min(count - 1, index))
      setCursor(next)
      focusRow(next)
    },
    [count, focusRow],
  )

  const onKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLTableSectionElement>) => {
      if (count === 0) return
      const target = event.target as HTMLElement
      // Escribir dentro de una celda editable no debe mover el cursor de fila.
      if (target.closest("input, textarea, select, [contenteditable='true']")) return

      const row = target.closest<HTMLTableRowElement>("[data-row-index]")
      const index = row ? Number(row.dataset.rowIndex) : cursor

      switch (event.key) {
        case "ArrowDown":
          event.preventDefault()
          move(index + 1)
          return
        case "ArrowUp":
          event.preventDefault()
          move(index - 1)
          return
        case "PageDown":
          event.preventDefault()
          move(index + 12)
          return
        case "PageUp":
          event.preventDefault()
          move(index - 12)
          return
        case "Home":
          event.preventDefault()
          move(0)
          return
        case "End":
          event.preventDefault()
          move(count - 1)
          return
        case "ArrowRight":
          if (!onExpand) return
          event.preventDefault()
          onExpand(index, true)
          return
        case "ArrowLeft":
          if (!onExpand) return
          event.preventDefault()
          onExpand(index, false)
          return
        case "Enter":
          if (!onActivate || target.closest("button, a")) return
          event.preventDefault()
          onActivate(index)
          return
        case " ":
          if (!onToggle || target.closest("button, a")) return
          event.preventDefault()
          onToggle(index)
          return
        default:
      }
    },
    [count, cursor, move, onActivate, onToggle, onExpand],
  )

  /** Props para <TableBody>. */
  const bodyProps = {
    ref: bodyRef,
    onKeyDown,
  }

  /** Props para cada <TableRow>. */
  const rowProps = React.useCallback(
    (index: number) => ({
      tabIndex: index === cursor ? 0 : -1,
      "data-row-index": index,
      onFocus: () => setCursor(index),
      onMouseDown: () => setCursor(index),
    }),
    [cursor],
  )

  return { cursor, setCursor, bodyProps, rowProps, focusRow }
}
